/* ============================================================================
   QR — codigo QR como elemento del diseno (F3)
   ----------------------------------------------------------------------------
   El cliente pega su enlace (Instagram, WhatsApp, su web) y el QR se coloca en
   el diseno como un elemento mas: se mueve y se escala dentro de la zona.

   LA REGLA QUE MANDA AQUI: un QR no es un dibujo, es un CODIGO. Si se guarda
   como bitmap y despues se estira a 300 DPI, los modulos salen borrosos y el
   telefono deja de leerlo — o peor, lo lee mal. Por eso en el JSON no viaja la
   imagen: viajan la URL y el estilo, y el archivo de impresion lo REGENERA a
   la resolucion final. Lo que se ve en pantalla es solo una vista previa.

   Por que qr-code-styling y no dibujarlo a mano: la codificacion QR lleva
   correccion de errores Reed-Solomon, enmascarado y seleccion de version. Es
   exactamente el tipo de cosa que no se improvisa: un fallo sutil da un codigo
   que parece bien y no se lee.

   TAMANO MINIMO. Un QR impreso por debajo de ~1,5 cm no lo lee un movil con
   comodidad: cada modulo queda por debajo del medio milimetro. Se avisa, no se
   prohibe — hay lectores buenos y clientes tozudos.

   NIVEL DE CORRECCION. Se usa 'H' (30%) y no el minimo: estos codigos acaban
   impresos sobre tela, ceramica curva o acrilico, y muchas veces con un logo
   encima. Con 'H' el codigo sobrevive a un logo central y a una taza girada.
   ========================================================================== */

const QR = (() => {
'use strict';

const URL_LIB = 'https://cdn.jsdelivr.net/npm/qr-code-styling@1.5.0/lib/qr-code-styling.js';
let _promesa = null;

/* La libreria son ~64 ko y solo la necesita quien pone un QR: se baja bajo
   demanda, igual que Three.js. */
function charger() {
  if (typeof QRCodeStyling !== 'undefined') return Promise.resolve(true);
  if (_promesa) return _promesa;
  _promesa = new Promise(res => {
    const sc = document.createElement('script');
    sc.src = URL_LIB; sc.async = true;
    sc.onload  = () => res(typeof QRCodeStyling !== 'undefined');
    sc.onerror = () => { console.warn('[EDISAMP] qr-code-styling indisponible'); res(false); };
    document.head.appendChild(sc);
  });
  return _promesa;
}

/* ------------------------------- validacion ------------------------------ */
/* Se exige https. No es purismo: un QR con http en una taza es un enlace que
   dentro de un ano puede no existir o servir contenido inseguro, y el cliente
   ya no puede cambiarlo — esta impreso. */
function normaliserUrl(brut) {
  let v = String(brut || '').trim();
  if (!v) return { ok:false, motif:'Collez d’abord un lien.' };
  if (!/^[a-z][a-z0-9+.-]*:/i.test(v)) v = 'https://' + v;
  let u;
  try { u = new URL(v); } catch (e) {
    return { ok:false, motif:'Ce lien n’est pas valide.' };
  }
  if (u.protocol !== 'https:') {
    return { ok:false, motif:'Seuls les liens https:// sont acceptés — un lien '
                           + 'non sécurisé imprimé sur un produit ne se corrige plus.' };
  }
  if (!/\./.test(u.hostname)) {
    return { ok:false, motif:'Ce lien n’a pas l’air complet (exemple : '
                           + 'https://instagram.com/edisamp).' };
  }
  /* Cuanto mas largo el enlace, mas modulos y mas pequeno cada uno: a partir
     de ~300 caracteres deja de ser imprimible en pocos centimetros. */
  if (u.href.length > 300) {
    return { ok:false, motif:'Ce lien est trop long pour un QR imprimable. '
                           + 'Utilisez un lien raccourci.' };
  }
  return { ok:true, url: u.href };
}

/* --------------------------------- estilo -------------------------------- */
const STYLE_DEFAUT = {
  couleur: '#111111',
  fond:    '#FFFFFF',
  coinsArrondis: true,
  logo: null,          // dataUrl opcional
};

const styleComplet = s => Object.assign({}, STYLE_DEFAUT, s || {});

/* ------------------------------- generacion ------------------------------ */
/* Devuelve un dataUrl PNG cuadrado de `cote` px. La MISMA funcion sirve para
   la vista previa y para el archivo de impresion: si fueran dos caminos
   distintos acabarian dando codigos distintos. */
async function generer(url, style, cote) {
  const ok = await charger();
  if (!ok) throw new Error('Générateur de QR indisponible (connexion ?).');
  const s = styleComplet(style);
  const px = Math.max(64, Math.round(cote || 512));

  const opciones = {
    width: px, height: px, type: 'canvas', data: url,
    /* Sin margen el lector no encuentra el codigo: la « quiet zone » es parte
       de la norma, no decoracion. */
    margin: Math.round(px * 0.06),
    qrOptions: { errorCorrectionLevel: 'H' },
    dotsOptions: {
      color: s.couleur,
      type: s.coinsArrondis ? 'rounded' : 'square',
    },
    cornersSquareOptions: {
      color: s.couleur,
      type: s.coinsArrondis ? 'extra-rounded' : 'square',
    },
    cornersDotOptions: { color: s.couleur, type: s.coinsArrondis ? 'dot' : 'square' },
    backgroundOptions: { color: s.fond || 'transparent' },
  };
  if (s.logo) {
    opciones.image = s.logo;
    opciones.imageOptions = {
      crossOrigin: 'anonymous',
      /* El hueco del logo se descuenta de la correccion de errores. 0.28 con
         nivel H aun deja el codigo legible; mas que eso empieza a fallar. */
      imageSize: 0.28,
      margin: Math.round(px * 0.012),
      hideBackgroundDots: true,
    };
  }

  const qr = new QRCodeStyling(opciones);
  const blob = await qr.getRawData('png');
  return await new Promise((res, rej) => {
    const l = new FileReader();
    l.onload = () => res(l.result);
    l.onerror = rej;
    l.readAsDataURL(blob);
  });
}

/* ------------------------------ legibilidad ------------------------------ */
const CM_MIN = 1.5;

/* ¿Sale escaneable a este tamano impreso? */
function verifierTaille(cmCote) {
  return { ok: cmCote >= CM_MIN, cmMin: CM_MIN, cm: Math.round(cmCote * 10) / 10 };
}

return { charger, normaliserUrl, generer, verifierTaille, styleComplet,
         STYLE_DEFAUT, CM_MIN };
})();
